// WhatsApp Service
import { CONTACT_INFO } from '../utils/constants';

class WhatsAppService {
  constructor() {
    this.baseURL = 'https://api.whatsapp.com/send';
  }

  // ---------- Phone helpers ----------

  normalizeIndianPhoneNumber(phoneNumber) {
    const digits = String(phoneNumber ?? '').replace(/\D/g, '');

    if (!digits) return '';
    if (digits.length === 10) return `91${digits}`;
    if (digits.length === 11 && digits.startsWith('0')) {
      return `91${digits.slice(1)}`;
    }
    if (digits.length === 12 && digits.startsWith('91')) {
      return digits;
    }

    return digits;
  }

  getAdminWhatsAppNumber() {
    return this.normalizeIndianPhoneNumber(CONTACT_INFO?.WHATSAPP);
  }

  formatIndianDisplayPhone(phoneNumber) {
    const normalized = this.normalizeIndianPhoneNumber(phoneNumber);

    if (!normalized) return '';

    return `+91 ${normalized.slice(-10)}`;
  }

  // ---------- Common formatters ----------

  toTitle(value) {
    return String(value ?? '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  formatMoney(value) {
    const amount = Number(value);

    return Number.isFinite(amount)
      ? Math.round(amount).toLocaleString('en-IN')
      : '0';
  }

  formatDate(value) {
    const rawValue = String(value ?? '').trim();

    if (!rawValue) return '-';

    const isoDateMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const localDateMatch = rawValue.match(/^(\d{2})-(\d{2})-(\d{4})$/);

    let date;

    if (isoDateMatch) {
      date = new Date(
        Number(isoDateMatch[1]),
        Number(isoDateMatch[2]) - 1,
        Number(isoDateMatch[3])
      );
    } else if (localDateMatch) {
      date = new Date(
        Number(localDateMatch[3]),
        Number(localDateMatch[2]) - 1,
        Number(localDateMatch[1])
      );
    } else {
      date = new Date(rawValue);
    }

    if (Number.isNaN(date.getTime())) {
      return rawValue;
    }

    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  formatQuantity(value) {
    return String(value || '-')
      .replace(
        /([0-9]+(?:\.[0-9]+)?)\s*(?:PCS?|PC)\b/gi,
        '$1 pcs'
      )
      .replace(
        /([0-9]+(?:\.[0-9]+)?)\s*KG\b/gi,
        '$1 KG'
      )
      .replace(
        /([0-9]+(?:\.[0-9]+)?)\s*GM\b/gi,
        '$1 GM'
      );
  }

  getItemQuantity(item = {}) {
    const quantity =
      item.calculatedQuantity ??
      item.customizations?.quantity ??
      item.portion_size ??
      item.portionSize ??
      item.quantityText ??
      item.quantity;

    return this.formatQuantity(quantity);
  }

  // ---------- Order helpers ----------

  formatGuestSummary({ guestCount, menuType, menuPackage }) {
    if (!guestCount || typeof guestCount !== 'object') {
      return '-';
    }

    const veg = Number(guestCount.veg) || 0;
    const nonVeg = Number(guestCount.nonVeg) || 0;
    const jain = Number(guestCount.jain) || 0;

    const isMixedOrder =
      menuType === 'customized' ||
      menuPackage === 'nonveg_starters';

    const totalGuests =
      menuType === 'jain'
        ? jain
        : isMixedOrder
          ? veg + nonVeg
          : veg || nonVeg || jain;

    const guestParts = [];

    if (menuType === 'jain') {
      if (jain > 0) {
        guestParts.push(`Jain ${jain}`);
      }
    } else if (isMixedOrder) {
      if (veg > 0) {
        guestParts.push(`Veg ${veg}`);
      }

      if (nonVeg > 0) {
        guestParts.push(`Non-Veg ${nonVeg}`);
      }
    } else if (menuType === 'veg') {
      if (veg > 0) {
        guestParts.push(`Veg ${veg}`);
      }
    } else {
      if (veg > 0) {
        guestParts.push(`Veg ${veg}`);
      }

      if (nonVeg > 0) {
        guestParts.push(`Non-Veg ${nonVeg}`);
      }

      if (jain > 0) {
        guestParts.push(`Jain ${jain}`);
      }
    }

    if (totalGuests > 0) {
      guestParts.push(`Total ${totalGuests}`);
    }

    return guestParts.length
      ? guestParts.join(' / ')
      : '-';
  }

  getDisplayMealType({
    menuPackage,
    menuPackageName,
    mealType
  }) {
    const isVegOnlyPackage =
      ['veg_starters', 'veg_main_course'].includes(menuPackage) ||
      /^Only (Starter|Main Course) Veg$/i.test(
        String(menuPackageName || '')
      );

    const isMixedStarterPackage =
      menuPackage === 'nonveg_starters' ||
      /^Only Starter Non Veg$/i.test(
        String(menuPackageName || '')
      );

    if (isVegOnlyPackage) {
      return 'Veg';
    }

    if (isMixedStarterPackage) {
      return 'Veg + Non-Veg';
    }

    return this.toTitle(mealType) || '-';
  }

  formatCategoryDetails(detailsSource) {
    if (
      !detailsSource ||
      typeof detailsSource !== 'object'
    ) {
      return [];
    }

    return Object.values(detailsSource)
      .filter(
        (categoryItems) =>
          Array.isArray(categoryItems) &&
          categoryItems.length > 0
      )
      .flatMap((categoryItems) =>
        categoryItems
          .map((entry) => {
            const rawValue =
              typeof entry === 'string'
                ? entry
                : entry?.name || entry?.title || '';

            if (!rawValue) {
              return null;
            }

            const itemAndQuantityMatch = String(rawValue).match(
              /^(.*?)\s+-\s+(.+)$/
            );

            const explicitQuantity =
              typeof entry === 'object'
                ? entry.calculatedQuantity ??
                  entry.quantityText ??
                  entry.portion_size ??
                  entry.portionSize
                : '';

            return {
              item: itemAndQuantityMatch
                ? itemAndQuantityMatch[1].trim()
                : String(rawValue).trim(),

              quantity: this.formatQuantity(
                itemAndQuantityMatch
                  ? itemAndQuantityMatch[2].trim()
                  : explicitQuantity
              )
            };
          })
          .filter(Boolean)
      );
  }

  buildItemLines(items) {
    if (!Array.isArray(items) || items.length === 0) {
      return ['No items added'];
    }

    return items.flatMap((item, index) => {
      const detailsSource =
        item?.packageQuantityDetails ??
        item?.packageDetails ??
        item?.selectedItems ??
        item?.customizations?.selectedItems;

      const categoryDetails =
        this.formatCategoryDetails(detailsSource);

      if (categoryDetails.length > 0) {
        return categoryDetails.map(
          ({ item: itemName, quantity }) =>
            `• ${itemName} — ${quantity}`
        );
      }

      const itemName = String(
        item?.name ||
        item?.title ||
        `Item ${index + 1}`
      );

      return [
        `• ${itemName} — ${this.getItemQuantity(item)}`
      ];
    });
  }

  // ---------- WhatsApp order message ----------

  formatOrderDetails(orderData = {}) {
    const {
      items = [],
      totalPrice,
      subTotal,
      deliveryCharge,
      gstAmount,
      deliveryDate,
      deliveryTime,
      location,
      phoneNumber,
      menuType,
      menuPackage,
      menuPackageName,
      mealType,
      guestCount
    } = orderData;

    const customerPhone =
      this.formatIndianDisplayPhone(phoneNumber) ||
      String(phoneNumber || '-');

    const guestSummary = this.formatGuestSummary({
      guestCount,
      menuType,
      menuPackage
    });

    const displayMealType = this.getDisplayMealType({
      menuPackage,
      menuPackageName,
      mealType
    });

    const deliveryChargeText =
      /actual/i.test(String(deliveryCharge ?? ''))
        ? 'As per Actual'
        : String(deliveryCharge || 'As per Actual');

    const itemLines = this.buildItemLines(items);

    return [
      'Order Confirmed ✅',
      '',
      'Thank you for choosing BiteAffair. Your catering order has been successfully confirmed.',
      '',
      'Order Details',
      '',
      `Phone: ${customerPhone}`,
      `Location: ${String(location || '-')}`,
      `Delivery Date: ${this.formatDate(deliveryDate)}`,
      `Delivery Time: ${String(deliveryTime || '-')}`,
      // `Meal Type: ${displayMealType}`,
      `Guests: ${guestSummary}`,
      '',
      ...itemLines,
      '',
      'Bill Summary',
      '',
      `Subtotal: ₹${this.formatMoney(subTotal)}`,
      `Delivery Charges: ${deliveryChargeText}`,
      `GST (Incl. CGST): ₹${this.formatMoney(gstAmount)}`,
      '',
      `Grand Total: ₹${this.formatMoney(totalPrice)}`,
      '',
      'Our team will contact you shortly for delivery confirmation.',
      '',
      '',
      'Thank you ❤️',
      'Team BiteAffair'
    ].join('\n');
  }

  // ---------- WhatsApp URL ----------

  buildWhatsAppURL(phoneNumber, message) {
    const formattedPhone =
      this.normalizeIndianPhoneNumber(phoneNumber);

    const encodedMessage =
      encodeURIComponent(message);

    return (
      `${this.baseURL}` +
      `?phone=${formattedPhone}` +
      `&text=${encodedMessage}`
    );
  }

  // ---------- Send order details ----------

  async sendOrderDetails(phoneNumber, orderData) {
    try {
      const message =
        this.formatOrderDetails(orderData);

      const destinationPhone =
        this.getAdminWhatsAppNumber() ||
        phoneNumber;

      const whatsappURL =
        this.buildWhatsAppURL(
          destinationPhone,
          message
        );

      window.open(whatsappURL, '_blank');

      return {
        success: true,
        message: 'Order details sent to WhatsApp',
        whatsappURL
      };
    } catch (error) {
      console.error(
        'WhatsApp Service Error:',
        error
      );

      return {
        success: false,
        message: 'Failed to send WhatsApp message',
        error:
          error instanceof Error
            ? error.message
            : String(error)
      };
    }
  }

  // ---------- WhatsApp Business API payload ----------

  async sendViaBusinessAPI(
    phoneNumber,
    orderData
  ) {
    try {
      const message =
        this.formatOrderDetails(orderData);

      const payload = {
        messaging_product: 'whatsapp',
        to: this.normalizeIndianPhoneNumber(
          phoneNumber
        ),
        type: 'text',
        text: {
          body: message
        }
      };

      console.log(
        'Would send via Business API:',
        payload
      );

      return {
        success: true,
        message:
          'Order details prepared for WhatsApp Business API',
        payload
      };
    } catch (error) {
      console.error(
        'WhatsApp Business API Error:',
        error
      );

      return {
        success: false,
        message:
          'Failed to prepare WhatsApp Business API message',
        error:
          error instanceof Error
            ? error.message
            : String(error)
      };
    }
  }

  // ---------- Send OTP ----------

  async sendOTPViaWhatsApp(
    phoneNumber,
    otpCode
  ) {
    try {
      const message = [
        'BiteAffair Verification Code',
        '',
        `Your OTP is: ${otpCode}`,
        '',
        'Please enter this code to verify your order.',
        '',
        'Do not share this code with anyone.'
      ].join('\n');

      const whatsappURL =
        this.buildWhatsAppURL(
          phoneNumber,
          message
        );

      window.open(whatsappURL, '_blank');

      return {
        success: true,
        message: 'OTP sent via WhatsApp',
        otpCode
      };
    } catch (error) {
      console.error(
        'WhatsApp OTP Error:',
        error
      );

      return {
        success: false,
        message: 'Failed to send OTP via WhatsApp',
        error:
          error instanceof Error
            ? error.message
            : String(error)
      };
    }
  }
}

export const whatsappService =
  new WhatsAppService();

export default whatsappService;
